import { useEffect, useState, useRef, useCallback } from "react";
import "./AIDebugger.css";
import { X, CheckCircle2, XCircle, Download, FileJson, Square } from "lucide-react";

const STORAGE_KEY = "replay-drawer-width";
const DEFAULT_WIDTH = 30;
const MIN_WIDTH = 15; // percentage
const MAX_WIDTH = 70; // percentage

export type ReplayStepStatus = {
  scenario: string;
  scenarioName: string;
  attemptNumber: number;
  stepNumber: number;
  step: {
    inputMessage: string;
    persona: string;
    expectedMessageType: string;
    expectedDisposition: string;
    expectedMessageData: any;
  };
  status: "pending" | "running" | "success" | "failed";
  failureReason?: string;
  actualMessageType?: string;
  actualDisposition?: string;
};

type ReplayDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  currentStep?: ReplayStepStatus | null;
  completedSteps: ReplayStepStatus[];
  isComplete?: boolean;
  onStopReplay?: () => void;
  onOpenReplayModal?: () => void;
  isRunning?: boolean;
};

export const ReplayDrawer = ({
  isOpen,
  onClose,
  currentStep,
  completedSteps,
  isComplete = false,
  onStopReplay,
  onOpenReplayModal,
  isRunning = false,
}: ReplayDrawerProps) => {
  const [width, setWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseFloat(saved) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !drawerRef.current) return;

      const container = drawerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;

      // Clamp width between MIN_WIDTH and MAX_WIDTH
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  const formatJSON = (obj: any): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const exportToTabDelimited = () => {
    const allSteps: ReplayStepStatus[] = [...completedSteps];
    if (currentStep) {
      allSteps.push(currentStep);
    }

    // Sort: first by scenario name, then by attempt number, then by step number
    allSteps.sort((a, b) => {
      if (a.scenarioName !== b.scenarioName) {
        return a.scenarioName.localeCompare(b.scenarioName);
      }
      if (a.attemptNumber !== b.attemptNumber) {
        return a.attemptNumber - b.attemptNumber;
      }
      return a.stepNumber - b.stepNumber;
    });

    // Create header row
    const headers = [
      "Scenario",
      "Scenario Name",
      "Step #",
      "Attempt #",
      "Input Message",
      "Persona",
      "Expected Type",
      "Expected Disposition",
      "Expected Data",
      "Actual Type",
      "Actual Disposition",
      "Pass/Fail",
      "Failure Reason",
    ];

    // Create data rows
    const rows = allSteps.map((step) => {
      return [
        step.scenario || step.scenarioName,
        step.scenarioName,
        step.stepNumber.toString(),
        step.attemptNumber.toString(),
        step.step.inputMessage,
        step.step.persona || "",
        step.step.expectedMessageType,
        step.step.expectedDisposition,
        JSON.stringify(step.step.expectedMessageData),
        step.actualMessageType || "",
        step.actualDisposition || "",
        step.status === "success" ? "Pass" : "Fail",
        step.failureReason || "",
      ];
    });

    // Combine headers and rows
    const content = [headers, ...rows]
      .map((row) => row.map((cell) => {
        // Escape tabs and newlines, wrap in quotes if contains special characters
        const cellStr = String(cell || "");
        if (cellStr.includes("\t") || cellStr.includes("\n") || cellStr.includes('"')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join("\t"))
      .join("\n");

    // Create blob and download
    const blob = new Blob([content], { type: "text/tab-separated-values" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `replay-export-${new Date().toISOString().split("T")[0]}.tsv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const allSteps: ReplayStepStatus[] = [...completedSteps];
    if (currentStep) {
      allSteps.push(currentStep);
    }

    // Sort: first by scenario name, then by attempt number, then by step number
    allSteps.sort((a, b) => {
      if (a.scenarioName !== b.scenarioName) {
        return a.scenarioName.localeCompare(b.scenarioName);
      }
      if (a.attemptNumber !== b.attemptNumber) {
        return a.attemptNumber - b.attemptNumber;
      }
      return a.stepNumber - b.stepNumber;
    });

    // Create JSON objects
    const exportData = allSteps.map((step) => ({
      scenario: step.scenario || step.scenarioName,
      scenarioName: step.scenarioName,
      stepNumber: step.stepNumber,
      attemptNumber: step.attemptNumber,
      inputMessage: step.step.inputMessage,
      persona: step.step.persona || "",
      expectedType: step.step.expectedMessageType,
      expectedDisposition: step.step.expectedDisposition,
      expectedData: step.step.expectedMessageData,
      actualType: step.actualMessageType || "",
      actualDisposition: step.actualDisposition || "",
      passFail: step.status === "success" ? "Pass" : "Fail",
      failureReason: step.failureReason || "",
    }));

    // Create blob and download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `replay-export-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusPill = (status: ReplayStepStatus["status"], failureReason?: string) => {
    if (status === "success") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            borderRadius: "12px",
            backgroundColor: "#d4edda",
            color: "#155724",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          <CheckCircle2 size={12} />
          Success
        </span>
      );
    } else if (status === "failed") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            borderRadius: "12px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          <XCircle size={12} />
          Failed
        </span>
      );
    } else if (status === "running") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            borderRadius: "12px",
            backgroundColor: "#d1ecf1",
            color: "#0c5460",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          Running...
        </span>
      );
    } else {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            borderRadius: "12px",
            backgroundColor: "#e2e3e5",
            color: "#383d41",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          Pending
        </span>
      );
    }
  };

  const renderStep = (stepStatus: ReplayStepStatus, isCurrent: boolean = false) => {
    return (
      <div
        key={`${stepStatus.scenarioName}-${stepStatus.attemptNumber}-${stepStatus.stepNumber}`}
        style={{
        //   padding: "16px",
          borderBottom: "1px solid #F3F3F3",
          backgroundColor: isCurrent ? "#f8f9fa" : "white",
        }}
      >
        {/* Header with scenario name, step #, attempt #, and persona */}
        <div
          style={{
            padding: "12px",
            backgroundColor: isCurrent ? "#e7f3ff" : "#f8f9fa",
            border: `1px solid ${isCurrent ? "#b3d9ff" : "#e9ecef"}`,
            borderRadius: "4px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "4px",
            }}
          >
            <div>
              <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px" }}>
                {stepStatus.scenarioName} - Step {stepStatus.stepNumber} - Attempt {stepStatus.attemptNumber}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                Persona: {stepStatus.step.persona || "(none)"}
              </div>
            </div>
            {getStatusPill(stepStatus.status, stepStatus.failureReason)}
          </div>
          {stepStatus.failureReason && (
            <div
              style={{
                marginTop: "8px",
                padding: "8px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#856404",
              }}
            >
              <strong>Failure:</strong> {stepStatus.failureReason}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
            Input Message:
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {stepStatus.step.inputMessage}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
            Step JSON:
          </div>
          <pre
            style={{
              fontSize: "11px",
              padding: "8px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "4px",
              overflow: "auto",
              maxHeight: "200px",
              margin: 0,
            }}
          >
            {formatJSON(stepStatus.step)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={drawerRef}
      className="ai-debugger"
      style={{
        display: isOpen ? "flex" : "none",
        width: `${width}%`,
      }}
    >
      <div className="ai-debugger-resize-handle" onMouseDown={handleMouseDown} />
      <div className="ai-debugger-inner" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="ai-debugger-header">
          <span style={{ fontSize: "16px", fontWeight: "600" }}>Replay</span>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {(() => {
              const allSteps: ReplayStepStatus[] = [...completedSteps];
              if (currentStep) {
                allSteps.push(currentStep);
              }
              const hasSteps = allSteps.length > 0;
              
              return (
                <>
                  <div
                    title="Export TSV"
                    onClick={hasSteps ? exportToTabDelimited : undefined}
                    style={{
                      cursor: hasSteps ? "pointer" : "not-allowed",
                      opacity: hasSteps ? 1 : 0.5,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Download
                      size={18}
                      style={{
                        color: hasSteps ? "var(--text-color-primary, #333)" : "var(--text-color-secondary, #999)",
                      }}
                    />
                  </div>
                  <div
                    title="Export JSON"
                    onClick={hasSteps ? exportToJSON : undefined}
                    style={{
                      cursor: hasSteps ? "pointer" : "not-allowed",
                      opacity: hasSteps ? 1 : 0.5,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <FileJson
                      size={18}
                      style={{
                        color: hasSteps ? "var(--text-color-primary, #333)" : "var(--text-color-secondary, #999)",
                      }}
                    />
                  </div>
                  {isRunning && onStopReplay && onOpenReplayModal && (
                    <div
                      title="Stop Replay"
                      onClick={() => {
                        onStopReplay();
                        onOpenReplayModal();
                      }}
                      style={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Square
                        size={18}
                        style={{
                          color: "#dc3545",
                        }}
                      />
                    </div>
                  )}
                </>
              );
            })()}
            <div className="ai-debugger-close">
              <X size={16} onClick={onClose} style={{ cursor: "pointer" }} />
            </div>
          </div>
        </div>

        <div className="ai-debugger-content" style={{ flex: 1, overflowY: "auto" }}>
          {(() => {
            // Combine completed steps and current step, then sort by scenario, attempt, and step number
            const allSteps: ReplayStepStatus[] = [...completedSteps];
            if (currentStep) {
              allSteps.push(currentStep);
            }
            
            // Sort: first by scenario ID, then by attempt number, then by step number
            allSteps.sort((a, b) => {
              const aScenario = a.scenario || a.scenarioName;
              const bScenario = b.scenario || b.scenarioName;
              if (aScenario !== bScenario) {
                return aScenario.localeCompare(bScenario);
              }
              if (a.attemptNumber !== b.attemptNumber) {
                return a.attemptNumber - b.attemptNumber;
              }
              return a.stepNumber - b.stepNumber;
            });

            if (allSteps.length === 0) {
              return <div className="ai-debugger-empty">No replay steps yet</div>;
            }

            return allSteps.map((step) => {
              const isCurrent = currentStep && 
                (step.scenario || step.scenarioName) === (currentStep.scenario || currentStep.scenarioName) &&
                step.attemptNumber === currentStep.attemptNumber &&
                step.stepNumber === currentStep.stepNumber;
              return renderStep(step, !!isCurrent);
            });
          })()}
        </div>

        {isComplete && (
          <div
            style={{
              padding: "32px 16px",
              backgroundColor: "#28a745",
              borderTop: "3px solid #155724",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "#ffffff",
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            >
              Complete!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

